import React from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import {
  SUPPORT_EMAIL,
  COMPANY_NAME,
  WEBSITE_URL,
  SUPPORT_PHONE,
  COMPANY_ADDRESS_LINE1,
  COMPANY_ADDRESS_LINE2,
} from '../constants/contact'
import { Logo } from '../components/Logo'

export default function Terms() {
  const { user } = useAuth()

  return (
    <div className="min-h-screen bg-paybyphone-light">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to={user ? "/dashboard" : "/"} className="flex items-center">
            <Logo width={150} height={36} />
          </Link>
          {user && (
            <Link to="/dashboard" className="text-paybyphone-green hover:text-paybyphone-green/80">
              ← Back to Dashboard
            </Link>
          )}
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Terms and Conditions / Conditions Générales de Vente</h1>

          <div className="prose prose-lg max-w-none">
            <p className="text-gray-600 mb-6">
              <strong>Effective date:</strong> January 1, 2025<br />
              <strong>Last updated:</strong> August 5, 2026<br />
              <strong>Date d'entrée en vigueur :</strong> 1er janvier 2025<br />
              <strong>Dernière mise à jour :</strong> 5 août 2026
            </p>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">English version</h2>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. About the Service</h2>
              <p className="text-gray-700 mb-4">
                Welcome to {WEBSITE_URL} (the “Service”), operated by {COMPANY_NAME} (“Company”, “we”, “us”, or “our”).
                The Service allows users to locate parking areas, save the position of their vehicle, use parking-related features,
                and access premium or pay-per-use functionalities depending on the selected plan.
              </p>
              <p className="text-gray-700">
                By accessing or using the Service, you agree to be bound by these Terms and Conditions. If you do not agree to
                these Terms, you must not use the Service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Pricing Plans and Billing</h2>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">2.0 Price list (in euros, VAT included where applicable)</h3>
              <div className="overflow-x-auto mb-4">
                <table className="min-w-full border border-gray-200 text-sm text-gray-700">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="border border-gray-200 px-3 py-2 text-left">Plan</th>
                      <th className="border border-gray-200 px-3 py-2 text-left">Price</th>
                      <th className="border border-gray-200 px-3 py-2 text-left">Billing frequency</th>
                      <th className="border border-gray-200 px-3 py-2 text-left">Included features</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-200 px-3 py-2 font-medium">Pay As You Go</td>
                      <td className="border border-gray-200 px-3 py-2">€7.90 per started hour</td>
                      <td className="border border-gray-200 px-3 py-2">One-time charge for the selected duration</td>
                      <td className="border border-gray-200 px-3 py-2">Full app access for the selected duration: parking map, vehicle position saving, timers and notifications</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-200 px-3 py-2 font-medium">Daily subscription</td>
                      <td className="border border-gray-200 px-3 py-2">€14.99 per day</td>
                      <td className="border border-gray-200 px-3 py-2">Recurring, billed every day until cancelled</td>
                      <td className="border border-gray-200 px-3 py-2">Unlimited app access: parking map, GPS navigation, vehicle position saving, smart notifications, priority support</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-200 px-3 py-2 font-medium">Monthly subscription</td>
                      <td className="border border-gray-200 px-3 py-2">€49.99 per month</td>
                      <td className="border border-gray-200 px-3 py-2">Recurring, billed every month until cancelled</td>
                      <td className="border border-gray-200 px-3 py-2">Unlimited app access: parking map, GPS navigation, vehicle position saving, smart notifications, priority support</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-gray-700 mb-4">
                Prices are displayed in euros. For customers located outside the euro area, the equivalent amount in the local
                currency is shown at checkout before payment. The exact amount charged is always displayed before you confirm payment.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">2.1 Monthly subscription</h3>
              <p className="text-gray-700 mb-4">
                When you choose the monthly plan, you are billed €49.99 (or the equivalent amount displayed at checkout in your local
                currency) for one month of access to the Service. Unless otherwise stated at checkout, the monthly subscription renews
                automatically at the end of each billing period until cancelled.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">2.2 Daily subscription</h3>
              <p className="text-gray-700 mb-4">
                When you choose the daily plan, you are billed €14.99 (or the equivalent amount displayed at checkout in your local
                currency) for one day of access to the Service. Unless otherwise stated at checkout, the daily subscription renews
                automatically every day until cancelled.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">2.3 Standard subscription / normal subscription</h3>
              <p className="text-gray-700 mb-4">
                Any “normal subscription”, “standard subscription”, or similar wording used on the Service refers to the subscription
                offer presented to the user at checkout, which may be monthly or daily depending on the sales page or payment path used.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">2.4 Pay As You Go</h3>
              <p className="text-gray-700 mb-4">
                The Service may also offer a “Pay As You Go” formula, priced at €7.90 per started hour (or the equivalent amount
                displayed at checkout in your local currency), calculated according to the duration selected by the user and billed
                immediately as indicated at checkout.
              </p>
              <p className="text-gray-700 mb-4">
                IMPORTANT: when the Pay As You Go offer is presented through the specific SubDirect flow with a 24-hour trial/activation
                mechanism, the user expressly agrees that, after the selected parking duration and the 24-hour trial period, a recurring
                subscription starts automatically, for the same amount as the Pay As You Go duration selected and billed daily, unless
                cancelled before the end of the trial period. Where the amount selected reaches €40 or more, the subscription is monthly
                instead of daily. The exact amount and billing frequency are displayed before payment is confirmed.
              </p>
              <p className="text-gray-700 mb-4 font-bold">
                IMPORTANT: THE PAY AS YOU GO PRICE DOES NOT, UNDER ANY CIRCUMSTANCES, CORRESPOND TO THE PAYMENT OF THE PHYSICAL PARKING
                SPACE OR OF ANY PUBLIC OR PRIVATE PARKING FEE. IT ONLY CORRESPONDS TO THE COST OF USING THE APPLICATION, INCLUDING THE
                HOURLY SAVING OF THE VEHICLE POSITION ON THE USER'S PHONE AND IN OUR DATABASES.
              </p>
              <p className="text-gray-700 mb-4">
                The user remains solely responsible for paying any parking meter, public parking, private parking, road usage, fines,
                or other third-party parking charges that may apply in the real world.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">2.5 Trial / activation flows</h3>
              <p className="text-gray-700 mb-4">
                Certain sales flows may include an activation, card registration, free trial, or temporary trial offer before a paid
                subscription starts. In such cases, the exact amount charged, if any, the duration of the trial, and the post-trial
                billing conditions are those expressly shown to the user before payment validation.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">2.6 Payment methods and processors</h3>
              <p className="text-gray-700 mb-4">
                Payments may be processed through SystemPay and/or Stripe, depending on the subscription path, country, configuration,
                or technical routing used at the time of purchase. By submitting your payment information, you authorize the relevant
                payment processor to charge the selected plan or usage amount.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">2.7 Auto-renewal and immediate billing</h3>
              <p className="text-gray-700 mb-4">
                Daily and monthly subscriptions renew automatically unless cancelled before the next billing date. Pay As You Go usage
                is billed immediately based on the selected duration and does not constitute payment for a parking spot itself.
              </p>
              <p className="text-gray-700 mb-4">
                For avoidance of doubt, when a user selects the Pay As You Go formula through the SubDirect path that includes a trial,
                the user also authorizes the automatic start of a recurring subscription after the 24-hour trial, for the same amount as
                the duration selected and billed daily (monthly if that amount reaches €40 or more), unless the subscription is cancelled
                in time.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Cancellation and Refund Policy</h2>
              <p className="text-gray-700 mb-4">
                You may cancel your subscription at any time, free of charge, directly from your{' '}
                <Link to="/subscription" className="text-paybyphone-green hover:text-green-600 font-medium">subscription page</Link>{' '}
                (log in, then click “Cancel Subscription”), or by contacting support via our{' '}
                <Link to="/contact" className="text-paybyphone-green hover:text-green-600 font-medium">contact page</Link>.
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>For daily or monthly subscriptions, cancellation stops future renewals from the next billing cycle.</li>
                <li>Access remains active until the end of the already-paid period, unless otherwise stated in the offer.</li>
                <li>Amounts already charged for a billing period in progress are in principle not refunded on a pro-rata basis, without prejudice to your statutory rights, in particular the 14-day right of withdrawal described in section 4 and any mandatory consumer protection rights.</li>
                <li>For Pay As You Go usage, the billed amount corresponds to the application usage already initiated or consumed; it may nevertheless be refunded where the conditions of the right of withdrawal or other statutory rights are met.</li>
              </ul>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">3.1 How to request a refund</h3>
              <p className="text-gray-700 mb-4">
                Any refund request may be submitted via our <Link to="/contact" className="text-paybyphone-green hover:text-green-600 font-medium">contact page</Link> or
                by email at {SUPPORT_EMAIL}, indicating the email address used for the purchase and the date of the transaction.
                We reply within a maximum of 14 days. Approved refunds are issued using the same payment method as the original
                transaction, within 14 days of our decision.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Right of Withdrawal (14 days)</h2>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">4.1 General principle</h3>
              <p className="text-gray-700 mb-4">
                In accordance with European consumer protection rules (Directive 2011/83/EU) and, for customers in France, Articles
                L221-18 et seq. of the French Consumer Code, consumers have a period of <strong>14 days</strong> from the conclusion of
                the contract to exercise their right of withdrawal, without having to give any reason and without penalty.
              </p>
              <p className="text-gray-700 mb-4">
                To exercise this right, contact us within the 14-day period via our{' '}
                <Link to="/contact" className="text-paybyphone-green hover:text-green-600 font-medium">contact page</Link> or by email
                at {SUPPORT_EMAIL}, stating unambiguously your decision to withdraw. If you withdraw, we will refund the payments
                received from you using the same payment method as the original transaction, no later than 14 days from the day we are
                informed of your decision.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">4.2 Digital services supplied immediately</h3>
              <p className="text-gray-700 mb-4">
                Where you request that the supply of the Service begins immediately, before the end of the 14-day withdrawal period:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>if you withdraw before the Service has been fully performed, you remain entitled to withdraw but may owe us an amount proportional to the service already provided until you informed us of your withdrawal (Article L221-25 of the French Consumer Code);</li>
                <li>your right of withdrawal is lost only where the Service has been fully performed with your prior express consent and your acknowledgement that you would thereby lose your right of withdrawal (Article L221-28 of the French Consumer Code).</li>
              </ul>
              <p className="text-gray-700 mb-4">
                By validating your purchase, you expressly consent to the immediate supply of the digital service and acknowledge the
                effects described above on your right of withdrawal.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-blue-800 text-sm">
                  <strong>For customers located in France or the European Union, nothing in these Terms limits the mandatory rights granted by applicable consumer protection law.</strong>
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. User Responsibilities</h2>
              <p className="text-gray-700 mb-4">By using the Service, you agree to:</p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>Provide accurate and complete information when creating your account and making a payment.</li>
                <li>Keep your login credentials and payment access secure.</li>
                <li>Use the Service only for lawful purposes.</li>
                <li>Understand that the Service helps you locate or save parking-related information, but does not replace your obligation to comply with local parking rules.</li>
                <li>Pay all real-world parking fees, tickets, tolls, or penalties due to third parties.</li>
                <li>Not interfere with, damage, or attempt unauthorized access to the Service.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Service Availability</h2>
              <p className="text-gray-700 mb-4">
                We aim to provide continuous access to the Service, but availability is not guaranteed at all times. The Service may be
                temporarily unavailable because of maintenance, updates, technical incidents, payment provider issues, connectivity issues,
                or circumstances beyond our control.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Limitation of Liability</h2>
              <p className="text-gray-700 mb-4">
                To the maximum extent permitted by law, {COMPANY_NAME} shall not be liable for indirect, incidental, special,
                consequential, or punitive damages, including loss of profits, data, use, goodwill, or other intangible losses arising
                out of or related to your use of the Service. We are not responsible for parking availability, parking enforcement,
                parking prices charged by third parties, or any fines or penalties issued by public or private authorities.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Changes to the Terms</h2>
              <p className="text-gray-700 mb-4">
                We reserve the right to modify these Terms at any time. Any material changes may be notified by publication on this page
                and by updating the “Last updated” date. Continued use of the Service after such changes constitutes acceptance of the updated Terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Contact Information</h2>
              <p className="text-gray-700 mb-4">
                If you have any questions regarding these Terms and Conditions, please contact us:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-700">
                  <strong>{COMPANY_NAME}</strong><br />
                  {COMPANY_ADDRESS_LINE1}, {COMPANY_ADDRESS_LINE2}<br />
                  Website: {WEBSITE_URL}<br />
                  Email: <a href={`mailto:${SUPPORT_EMAIL}`} className="text-paybyphone-green hover:text-green-600 font-medium">{SUPPORT_EMAIL}</a> — {SUPPORT_PHONE}<br />
                  Contact form: <Link to="/contact" className="text-paybyphone-green hover:text-green-600 font-medium">{WEBSITE_URL}/contact</Link><br />
                  Legal information: <Link to="/legal" className="text-paybyphone-green hover:text-green-600 font-medium">{WEBSITE_URL}/legal</Link>
                </p>
              </div>
            </section>

            <hr className="my-10" />

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Version française</h2>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. À propos du service</h2>
              <p className="text-gray-700 mb-4">
                Bienvenue sur {WEBSITE_URL} (le « Service »), exploité par {COMPANY_NAME} (« Société », « nous », « notre » ou « nos »).
                Le Service permet notamment aux utilisateurs de localiser des zones de stationnement, de sauvegarder la position de leur
                véhicule, d'utiliser des fonctionnalités liées au parking et d'accéder à des offres premium ou à l'usage selon la formule choisie.
              </p>
              <p className="text-gray-700">
                En accédant au Service ou en l'utilisant, vous acceptez d'être lié par les présentes Conditions Générales de Vente et
                d'Utilisation. Si vous n'acceptez pas ces conditions, vous ne devez pas utiliser le Service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Offres, tarification et facturation</h2>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">2.0 Grille tarifaire (en euros, TTC le cas échéant)</h3>
              <div className="overflow-x-auto mb-4">
                <table className="min-w-full border border-gray-200 text-sm text-gray-700">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="border border-gray-200 px-3 py-2 text-left">Offre</th>
                      <th className="border border-gray-200 px-3 py-2 text-left">Prix</th>
                      <th className="border border-gray-200 px-3 py-2 text-left">Fréquence de facturation</th>
                      <th className="border border-gray-200 px-3 py-2 text-left">Fonctionnalités incluses</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-200 px-3 py-2 font-medium">Pay As You Go</td>
                      <td className="border border-gray-200 px-3 py-2">7,90 € par heure entamée</td>
                      <td className="border border-gray-200 px-3 py-2">Paiement ponctuel pour la durée choisie</td>
                      <td className="border border-gray-200 px-3 py-2">Accès complet à l'application pendant la durée choisie : carte des stationnements, sauvegarde de la position du véhicule, minuteurs et notifications</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-200 px-3 py-2 font-medium">Abonnement journalier</td>
                      <td className="border border-gray-200 px-3 py-2">14,99 € par jour</td>
                      <td className="border border-gray-200 px-3 py-2">Récurrent, facturé chaque jour jusqu'à annulation</td>
                      <td className="border border-gray-200 px-3 py-2">Accès illimité : carte des stationnements, navigation GPS, sauvegarde de la position du véhicule, notifications intelligentes, support prioritaire</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-200 px-3 py-2 font-medium">Abonnement mensuel</td>
                      <td className="border border-gray-200 px-3 py-2">49,99 € par mois</td>
                      <td className="border border-gray-200 px-3 py-2">Récurrent, facturé chaque mois jusqu'à annulation</td>
                      <td className="border border-gray-200 px-3 py-2">Accès illimité : carte des stationnements, navigation GPS, sauvegarde de la position du véhicule, notifications intelligentes, support prioritaire</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-gray-700 mb-4">
                Les prix sont affichés en euros. Pour les clients situés hors de la zone euro, le montant équivalent dans la devise
                locale est affiché au moment du paiement. Le montant exact débité est toujours affiché avant la validation du paiement.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">2.1 Abonnement mensuel</h3>
              <p className="text-gray-700 mb-4">
                Lorsque vous choisissez la formule mensuelle, 49,99 € (ou le montant équivalent affiché au moment du paiement dans votre
                devise locale) sont facturés pour un mois d'accès au Service. Sauf indication contraire au moment du paiement,
                l'abonnement mensuel est reconduit automatiquement à chaque échéance jusqu'à annulation.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">2.2 Abonnement journalier</h3>
              <p className="text-gray-700 mb-4">
                Lorsque vous choisissez la formule journalière, 14,99 € (ou le montant équivalent affiché au moment du paiement dans
                votre devise locale) sont facturés pour une journée d'accès au Service. Sauf indication contraire, l'abonnement
                journalier est reconduit automatiquement chaque jour jusqu'à annulation.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">2.3 Abonnement normal / standard</h3>
              <p className="text-gray-700 mb-4">
                Toute mention d'« abonnement normal », « abonnement standard » ou formulation équivalente sur le Service désigne l'offre
                d'abonnement présentée à l'utilisateur lors du paiement, laquelle peut être mensuelle ou journalière selon la page de vente
                ou le parcours de paiement utilisé.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">2.4 Pay As You Go</h3>
              <p className="text-gray-700 mb-4">
                Le Service peut aussi proposer une formule « Pay As You Go » au prix de 7,90 € par heure entamée (ou le montant
                équivalent affiché au moment du paiement dans votre devise locale), calculé selon la durée choisie par l'utilisateur
                et débité immédiatement selon les indications affichées au moment du paiement.
              </p>
              <p className="text-gray-700 mb-4">
                IMPORTANT : lorsque l'offre Pay As You Go est proposée via le parcours spécifique SubDirect avec un mécanisme d'essai ou
                d'activation de 24 heures, l'utilisateur accepte expressément qu'à l'issue de la durée de stationnement choisie et de la
                période d'essai de 24 heures, un abonnement récurrent débute automatiquement, du même montant que la durée Pay As You Go
                choisie et facturé chaque jour, sauf annulation avant la fin de la période d'essai. Lorsque le montant choisi atteint
                40 € ou plus, l'abonnement est mensuel et non journalier. Le montant exact et la fréquence de facturation sont affichés
                avant la validation du paiement.
              </p>
              <p className="text-gray-700 mb-4 font-bold">
                IMPORTANT : LE PAY AS YOU GO NE CORRESPOND EN AUCUN CAS AU PAIEMENT DU STATIONNEMENT LUI-MÊME, NI À UN DROIT DE PLACE,
                NI À UN HORODATEUR, NI À DES FRAIS DE PARKING PUBLIC OU PRIVÉ. IL CORRESPOND UNIQUEMENT AU COÛT D'UTILISATION DE
                L'APPLICATION, NOTAMMENT POUR LA SAUVEGARDE DE LA POSITION DU VÉHICULE PAR HEURE, DANS LE TÉLÉPHONE DE
                L'UTILISATEUR ET DANS NOS BASES DE DONNÉES.
              </p>
              <p className="text-gray-700 mb-4">
                L'utilisateur reste seul responsable du paiement de tout stationnement réel, horodateur, parking public, parking privé,
                amende, redevance ou frais dus à des tiers dans le monde réel.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">2.5 Parcours d'essai ou d'activation</h3>
              <p className="text-gray-700 mb-4">
                Certains parcours de vente peuvent prévoir une activation, un enregistrement de carte, un essai gratuit ou une offre d'essai
                temporaire avant le démarrage d'un abonnement payant. Dans ce cas, le montant éventuellement débité, la durée de l'essai
                et les conditions de facturation après essai sont celles expressément affichées à l'utilisateur avant la validation du paiement.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">2.6 Moyens de paiement et prestataires</h3>
              <p className="text-gray-700 mb-4">
                Les paiements peuvent être traités par SystemPay et/ou Stripe, selon le parcours de souscription, le pays, la configuration
                technique ou le routage utilisé au moment de l'achat. En renseignant vos informations de paiement, vous autorisez le
                prestataire de paiement concerné à débiter le montant de l'offre ou de l'usage sélectionné.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">2.7 Reconduction automatique et débit immédiat</h3>
              <p className="text-gray-700 mb-4">
                Les abonnements journaliers et mensuels se renouvellent automatiquement sauf annulation avant la prochaine date de facturation.
                Le Pay As You Go est débité immédiatement en fonction de la durée choisie et ne constitue jamais le paiement d'une place de parking elle-même.
              </p>
              <p className="text-gray-700 mb-4">
                Afin d'éviter toute ambiguïté, lorsqu'un utilisateur choisit la formule Pay As You Go via le parcours SubDirect incluant
                un essai, il autorise également le démarrage automatique d'un abonnement récurrent après les 24 heures d'essai, du même
                montant que la durée choisie et facturé chaque jour (mensuel si ce montant atteint 40 € ou plus), sauf annulation
                effectuée à temps.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Politique d'annulation et de remboursement</h2>
              <p className="text-gray-700 mb-4">
                Vous pouvez annuler votre abonnement à tout moment, sans frais, directement depuis votre{' '}
                <Link to="/subscription" className="text-paybyphone-green hover:text-green-600 font-medium">page abonnement</Link>{' '}
                (connectez-vous puis cliquez sur « Annuler l'abonnement »), ou en contactant le support via notre{' '}
                <Link to="/contact" className="text-paybyphone-green hover:text-green-600 font-medium">page contact</Link>.
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>Pour les abonnements journaliers ou mensuels, l'annulation met fin aux renouvellements futurs à compter du prochain cycle.</li>
                <li>L'accès au Service reste actif jusqu'à la fin de la période déjà payée, sauf indication contraire dans l'offre.</li>
                <li>Les montants déjà facturés pour une période en cours ne donnent en principe pas lieu à un remboursement prorata temporis, sans préjudice de vos droits légaux, notamment du droit de rétractation de 14 jours décrit à l'article 4 et des dispositions impératives du droit de la consommation.</li>
                <li>Pour le Pay As You Go, le montant facturé correspond à un usage de l'application déjà initié ou consommé ; il peut néanmoins être remboursé lorsque les conditions du droit de rétractation ou d'autres droits légaux sont réunies.</li>
              </ul>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">3.1 Comment demander un remboursement</h3>
              <p className="text-gray-700 mb-4">
                Toute demande de remboursement peut être adressée via notre{' '}
                <Link to="/contact" className="text-paybyphone-green hover:text-green-600 font-medium">page contact</Link> ou par email
                à {SUPPORT_EMAIL}, en précisant l'adresse email utilisée lors de l'achat et la date de la transaction. Nous répondons
                sous 14 jours maximum. Les remboursements acceptés sont effectués via le même moyen de paiement que la transaction
                initiale, dans un délai de 14 jours suivant notre décision.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Droit de rétractation (14 jours)</h2>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">4.1 Principe général</h3>
              <p className="text-gray-700 mb-4">
                Conformément aux règles européennes de protection des consommateurs (directive 2011/83/UE) et, pour les clients situés
                en France, aux articles L221-18 et suivants du Code de la consommation, le consommateur dispose d'un délai de{' '}
                <strong>14 jours</strong> à compter de la conclusion du contrat pour exercer son droit de rétractation, sans avoir à
                motiver sa décision ni à supporter de pénalité.
              </p>
              <p className="text-gray-700 mb-4">
                Pour exercer ce droit, contactez-nous dans le délai de 14 jours via notre{' '}
                <Link to="/contact" className="text-paybyphone-green hover:text-green-600 font-medium">page contact</Link> ou par email
                à {SUPPORT_EMAIL}, en exprimant sans ambiguïté votre volonté de vous rétracter. En cas de rétractation, nous vous
                remboursons les paiements reçus via le même moyen de paiement que la transaction initiale, au plus tard 14 jours à
                compter du jour où nous sommes informés de votre décision.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">4.2 Services numériques fournis immédiatement</h3>
              <p className="text-gray-700 mb-4">
                Lorsque vous demandez que la fourniture du Service commence immédiatement, avant l'expiration du délai de rétractation
                de 14 jours :
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>si vous vous rétractez avant que le Service ait été entièrement exécuté, vous conservez votre droit de rétractation mais pouvez être redevable d'un montant proportionnel au service déjà fourni jusqu'à la communication de votre décision (article L221-25 du Code de la consommation) ;</li>
                <li>votre droit de rétractation n'est perdu que lorsque le Service a été entièrement exécuté avec votre accord préalable exprès et votre reconnaissance de la perte de ce droit (article L221-28 du Code de la consommation).</li>
              </ul>
              <p className="text-gray-700 mb-4">
                En validant votre achat, vous consentez expressément à la fourniture immédiate du service numérique et reconnaissez les
                effets décrits ci-dessus sur votre droit de rétractation.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-blue-800 text-sm">
                  <strong>Pour les clients situés en France ou dans l'Union européenne, aucune stipulation des présentes conditions ne limite les droits impératifs conférés par le droit de la consommation applicable.</strong>
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Responsabilités de l'utilisateur</h2>
              <p className="text-gray-700 mb-4">En utilisant le Service, vous vous engagez à :</p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>Fournir des informations exactes et complètes lors de la création de votre compte et lors du paiement.</li>
                <li>Préserver la sécurité de vos identifiants et de vos accès de paiement.</li>
                <li>Utiliser le Service uniquement à des fins légales.</li>
                <li>Comprendre que le Service vous aide à localiser ou sauvegarder des informations liées au stationnement, sans vous dispenser du respect des règles locales de stationnement.</li>
                <li>Payer tous les frais réels de stationnement, horodateurs, contraventions, péages ou pénalités dus à des tiers.</li>
                <li>Ne pas perturber, endommager ou tenter d'accéder sans autorisation au Service.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Disponibilité du service</h2>
              <p className="text-gray-700 mb-4">
                Nous faisons nos meilleurs efforts pour assurer l'accès continu au Service, sans toutefois garantir sa disponibilité permanente.
                Le Service peut être temporairement indisponible en raison d'opérations de maintenance, de mises à jour, d'incidents techniques,
                de problèmes liés aux prestataires de paiement, de connectivité ou de circonstances indépendantes de notre volonté.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Limitation de responsabilité</h2>
              <p className="text-gray-700 mb-4">
                Dans la mesure maximale permise par la loi, {COMPANY_NAME} ne pourra être tenue responsable des dommages indirects,
                accessoires, spéciaux, consécutifs ou punitifs, y compris les pertes de profits, de données, d'usage, de clientèle ou
                d'autres pertes immatérielles résultant de l'utilisation du Service. Nous ne sommes pas responsables de la disponibilité
                réelle des stationnements, du contrôle du stationnement, des tarifs de parking pratiqués par des tiers, ni des amendes ou
                pénalités émises par des autorités publiques ou privées.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Modification des conditions</h2>
              <p className="text-gray-700 mb-4">
                Nous nous réservons le droit de modifier les présentes conditions à tout moment. Toute modification substantielle peut être
                portée à votre connaissance par publication sur cette page et par mise à jour de la date de « Dernière mise à jour ».
                La poursuite de l'utilisation du Service après modification vaut acceptation des conditions mises à jour.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Informations de contact</h2>
              <p className="text-gray-700 mb-4">
                Si vous avez des questions concernant les présentes Conditions Générales de Vente et d'Utilisation, vous pouvez nous contacter :
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-700">
                  <strong>{COMPANY_NAME}</strong><br />
                  {COMPANY_ADDRESS_LINE1}, 98701 Arue, Polynésie française<br />
                  Site web : {WEBSITE_URL}<br />
                  Email : <a href={`mailto:${SUPPORT_EMAIL}`} className="text-paybyphone-green hover:text-green-600 font-medium">{SUPPORT_EMAIL}</a> — {SUPPORT_PHONE}<br />
                  Formulaire de contact : <Link to="/contact" className="text-paybyphone-green hover:text-green-600 font-medium">{WEBSITE_URL}/contact</Link><br />
                  Mentions légales : <Link to="/legal" className="text-paybyphone-green hover:text-green-600 font-medium">{WEBSITE_URL}/legal</Link>
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  )
}
